import { RowCrudStatus } from "../../common/types";
import {
  EvaluationQuery,
  EvaluationQueryCreateMutationVariables,
  EvaluationQueryUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { EvaluationPathType } from "../../../api/graphql";
import { EvaluationDifficulty } from "../../../api/graphql";
import { safeJsonParse } from "../../../common/utils";
import { blankToNull } from "../../../common/blankToNull";
import { EvaluationAmbiguityType } from "../../../api/graphql";

export type EvaluationQueryValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  query: string;
  pathType: EvaluationPathType;
  difficulty: EvaluationDifficulty;
  expectedTool: string;
  expectedArgs: string;
  acceptableTools: string;
  expectedResponsePattern: string;
  generatedFrom: string;
  isAmbiguous: boolean;
  ambiguityType: EvaluationAmbiguityType | "";
  disambiguationResponse: string;
  notes: string;
};

export type EvaluationQueryErrorsType = {
  query?: { message: string; fieldLabel: string };
  pathType?: { message: string; fieldLabel: string };
  difficulty?: { message: string; fieldLabel: string };
  expectedTool?: { message: string; fieldLabel: string };
  expectedArgs?: { message: string; fieldLabel: string };
  acceptableTools?: { message: string; fieldLabel: string };
  expectedResponsePattern?: { message: string; fieldLabel: string };
  generatedFrom?: { message: string; fieldLabel: string };
  isAmbiguous?: { message: string; fieldLabel: string };
  ambiguityType?: { message: string; fieldLabel: string };
  disambiguationResponse?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
};

export function EvaluationQueryValuesInit(): EvaluationQueryValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    query: "",
    pathType: "" as EvaluationPathType,
    difficulty: "" as EvaluationDifficulty,
    expectedTool: "",
    expectedArgs: "{}",
    acceptableTools: "{}",
    expectedResponsePattern: "",
    generatedFrom: "{}",
    isAmbiguous: false,
    ambiguityType: "" as EvaluationAmbiguityType | "",
    disambiguationResponse: "",
    notes: "",
  };
}

export type EvaluationQueryType = EvaluationQuery | EvaluationQueryValuesType;

export function evaluationQueryDisplayId(
  source: Partial<EvaluationQuery>,
  _langStore: LangStore
) {
  return (
    (source.pathType ?? _langStore.emptyStringSymbol) +
    "-" +
    (source.difficulty ?? _langStore.emptyStringSymbol) +
    "-" +
    (source.expectedTool ?? _langStore.emptyStringSymbol) +
    "-" +
    (source.isAmbiguous ?? _langStore.emptyStringSymbol)
  );
}

export function getEvaluationQueryValues(
  source: EvaluationQueryType,
  _langStore: LangStore,
  _duplicated: boolean = false
): EvaluationQueryValuesType {
  const entityValues = EvaluationQueryValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : evaluationQueryDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.query = source.query;
  entityValues.pathType = source.pathType;
  entityValues.difficulty = source.difficulty;
  entityValues.expectedTool = source.expectedTool;
  entityValues.expectedArgs = source.expectedArgs;
  entityValues.acceptableTools = source.acceptableTools;
  entityValues.expectedResponsePattern = source.expectedResponsePattern || "";
  entityValues.generatedFrom = source.generatedFrom;
  entityValues.isAmbiguous = source.isAmbiguous || false;
  entityValues.ambiguityType =
    source.ambiguityType || ("" as EvaluationAmbiguityType | "");
  entityValues.disambiguationResponse = source.disambiguationResponse || "";
  entityValues.notes = source.notes || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getEvaluationQueryUpdateVariables(
  values: EvaluationQueryValuesType
): EvaluationQueryUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    query: values.query,
    pathType: values.pathType,
    difficulty: values.difficulty,
    expectedTool: values.expectedTool,
    expectedArgs: safeJsonParse(values.expectedArgs, "expectedArgs"),
    acceptableTools: safeJsonParse(values.acceptableTools, "acceptableTools"),
    expectedResponsePattern: blankToNull(values.expectedResponsePattern),
    generatedFrom: safeJsonParse(values.generatedFrom, "generatedFrom"),
    isAmbiguous: values.isAmbiguous,
    ambiguityType: values.ambiguityType || null,
    disambiguationResponse: blankToNull(values.disambiguationResponse),
    notes: blankToNull(values.notes),
  };
}

export function getEvaluationQueryCreateVariables(
  values: EvaluationQueryValuesType
): EvaluationQueryCreateMutationVariables {
  return {
    query: values.query,
    pathType: values.pathType,
    difficulty: values.difficulty,
    expectedTool: values.expectedTool,
    expectedArgs: safeJsonParse(values.expectedArgs, "expectedArgs"),
    acceptableTools: safeJsonParse(values.acceptableTools, "acceptableTools"),
    expectedResponsePattern: blankToNull(values.expectedResponsePattern),
    generatedFrom: safeJsonParse(values.generatedFrom, "generatedFrom"),
    isAmbiguous: values.isAmbiguous,
    ambiguityType: values.ambiguityType || null,
    disambiguationResponse: blankToNull(values.disambiguationResponse),
    notes: blankToNull(values.notes),
  };
}
